import { useEffect, useMemo, useRef, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { CustomButton } from "../../CustomButton";
import { ChartIcon } from "components/images";
import { BaseModal } from "../../BaseModal";
import { Chart } from "react-chartjs-2";
import { useDebounceValue, useStateWithSearchParams } from "../../../../hooks";
import { useLazyGetBubbleChartQuery } from "../../../../api";
import { QUERY_SEPARATOR } from "../../../../constant";
import { IndicatorTimeframe, Order } from "../../../../types";
import { KeyRoles } from "../../../../types/enums";
import { keyRolesItems, priorityActionTags } from "../../../../types/genereal";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
  BarElement,
  BarController,
  ChartOptions,
  ChartData,
  type ChartTypeRegistry,
} from "chart.js";

import { ReuseTooltip } from "../../ReuseTooltip";
import { BubbleLegendBlock } from "./BubbleLegendBlock";
import { ChartAnalyticsBlock } from "./ChartAnalyticsBlock";
import { PersonaGapChart } from "./PersonaGapChart";
import { PriorityActionType } from "../../../../types/apiTypes";

ChartJS.register(
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
  BarElement,
  BarController,
);

export type BubbleChartItem = {
  role: KeyRoles;
  expected: number;
  actual: number;
  weight: number;
  label: string;
  color: string;
  coverage: number;
};

type SearchParams = {
  sizes?: string;
  industries?: string;
  regions?: string;
  search?: string;
  sort?: string;
  page?: string;
  per_page?: string;
  selected?: string;
  limit?: string;
  date?: string;
  period?: string;
};

type ChartTab = "role" | "persona";
const CHART_AREA_HEIGHT = "50vh";

const getDiagonalHatchSVG = ({
  bgColor = "transparent",
  lineColor = "#000",
  lineWidth = 1,
  spacing = 4,
  rotate = -45,
} = {}) => {
  return `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" preserveAspectRatio="none">
  <defs>
    <pattern id="diagonalHatch" patternUnits="userSpaceOnUse" width="${spacing}" height="${spacing}" patternTransform="rotate(${rotate})">
      <line x1="0" y1="0" x2="0" y2="${spacing}" stroke="${lineColor}" stroke-width="${lineWidth}" />
    </pattern>
  </defs>
  <rect width="100%" height="100%" fill="${bgColor}" />
  <rect width="100%" height="100%" fill="url(#diagonalHatch)" />
</svg>
  `.trim();
};

const chartTabs: { key: ChartTab; label: string }[] = [
  { key: "role", label: "Role" },
  { key: "persona", label: "Persona" },
];

export const StackChartBtn = ({
  isAccounts,
  id,
  isDisabled,
  isNotActiveRole,
  title = "Targeted Accounts",
}: {
  isAccounts?: boolean;
  id: string;
  isDisabled: boolean;
  isNotActiveRole: boolean;
  title?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<ChartTab>("role");
  const [modalContentEl, setModalContentEl] = useState<HTMLDivElement | null>(
    null,
  );
  const chartRef = useRef<ChartJS>(null);

  const [selectedRole, setSelectedRole] = useState<string>("");
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const activeIndexRef = useRef<number | null>(null);
  const legendHoverActiveRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setTab("role");
    }
    if (!isOpen && tooltipRef.current) {
      setSelectedRole("");
      tooltipRef.current.style.display = "none";
      activeIndexRef.current = null;
      tooltipRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (tab !== "role" && tooltipRef.current) {
      tooltipRef.current.style.display = "none";
      activeIndexRef.current = null;
      setActiveIndex(null);
      setSelectedRole("");
    }
  }, [tab]);

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    industries: undefined,
    regions: undefined,
    sizes: undefined,
    search: undefined,
    page: undefined,
    per_page: undefined,
    selected: undefined,
    limit: undefined,
    date: undefined,
    period: undefined,
    sort: undefined,
  });

  const searchValueDebounce = useDebounceValue(searchDict.search || "");

  const [getBubbleChart, { data: currentData, isLoading }] =
    useLazyGetBubbleChartQuery();

  const chartData: BubbleChartItem[] = useMemo(() => {
    return (currentData?.roles || []).map((el) => ({
      ...el,
      label: keyRolesItems[el.role].title,
      color: keyRolesItems[el.role].color,
    }));
  }, [currentData]);

  useEffect(() => {
    const idx = chartData.findIndex((el) => el.label === selectedRole);
    activeIndexRef.current = idx === -1 ? null : idx;
    setActiveIndex(idx === -1 ? null : idx);
  }, [chartData, selectedRole]);

  const labels = chartData.map((el) => el.label);

  const data: ChartData<"bar"> = {
    labels: chartData.map((el) => el.label),
    datasets: [
      {
        label: "Expected",
        data: chartData.map(({ actual, expected }) =>
          actual > expected ? actual : expected,
        ),
        backgroundColor: chartData.map(() => "transparent"),
      },
    ],
  };

  const lightenColor = (color: string, percent: number) => {
    const num = parseInt(color.replace("#", ""), 16);
    const r = Math.min(255, (num >> 16) + 255 * percent);
    const g = Math.min(255, ((num >> 8) & 0x00ff) + 255 * percent);
    const b = Math.min(255, (num & 0x0000ff) + 255 * percent);
    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
  };

  const customBarPlugin = {
    id: "customBarPlugin",

    afterEvent(chart: any, args: any) {
      const { canvas, scales } = chart;
      const event = args.event;

      let foundIndex: number | null = null;

      chartData.forEach(({ actual, expected }, index) => {
        const x = scales.x.getPixelForValue(index);
        const topY = scales.y.getPixelForValue(
          actual > expected ? actual : expected,
        );
        const bottomY = scales.y.getPixelForValue(0);
        const barWidth =
          scales.x.getPixelForValue(1) - scales.x.getPixelForValue(0) - 12;

        if (
          event.x >= x - barWidth / 2 &&
          event.x <= x + barWidth / 2 &&
          event.y >= topY &&
          event.y <= bottomY
        ) {
          foundIndex = index;
        }
      });

      if (foundIndex !== null) {
        activeIndexRef.current = foundIndex;
        setActiveIndex(foundIndex);
        setSelectedRole(chartData[foundIndex].label);
        chart.activeBarIndex = foundIndex;
        canvas.style.cursor = "pointer";
      } else {
        canvas.style.cursor = "default";

        if (!legendHoverActiveRef.current) {
          activeIndexRef.current = null;
          setActiveIndex(null);
          setSelectedRole("");
          chart.activeBarIndex = undefined;
        }
      }
    },

    afterDraw(chart: any) {
      const { ctx, scales } = chart;

      chartData.forEach(async ({ color, expected, actual }, index) => {
        const x = scales.x.getPixelForValue(index);
        const actualY = scales.y.getPixelForValue(actual);
        const expectedY = scales.y.getPixelForValue(expected);
        const underY = scales.y.getPixelForValue(actual - (actual - expected));
        const y0 = scales.y.getPixelForValue(0);

        const barWidth =
          scales.x.getPixelForValue(1) - scales.x.getPixelForValue(0) - 12;

        const isActive =
          activeIndexRef.current === null
            ? true
            : activeIndexRef.current === index;

        const fillColor = isActive ? color : lightenColor(color, 0.15);

        const isOver = actual > expected;

        ctx.save();
        ctx.fillStyle = fillColor;
        ctx.fillRect(
          x - barWidth / 2,
          isOver ? underY : actualY,
          barWidth,
          y0 - (isOver ? underY : actualY),
        );
        ctx.strokeStyle = fillColor;
        ctx.strokeRect(
          x - barWidth / 2,
          isOver ? underY : actualY,
          barWidth,
          y0 - (isOver ? underY : actualY),
        );
        ctx.restore();

        ctx.save();
        const expectedColor = isActive
          ? "#FE8836"
          : lightenColor("#FE8836", 0.15);
        ctx.fillStyle = expectedColor;
        ctx.fillRect(x - barWidth / 2, expectedY, barWidth, 4);
        ctx.strokeStyle = expectedColor;
        ctx.strokeRect(x - barWidth / 2, expectedY, barWidth, 4);
        ctx.restore();

        if (isOver) {
          const height = actualY - expectedY;
          const left = x - barWidth / 2;

          const img = new Image();
          img.src =
            "data:image/svg+xml;base64," +
            btoa(
              getDiagonalHatchSVG({
                bgColor: "transparent",
                lineColor: fillColor,
                lineWidth: 1,
                spacing: 1,
                rotate: -45,
              }),
            );

          const pattern = ctx.createPattern(img, "repeat");

          if (pattern) {
            ctx.save();
            ctx.fillStyle = pattern;
            ctx.fillRect(left, expectedY, barWidth, height);
            ctx.restore();
          }

          ctx.save();
          ctx.strokeStyle = fillColor;
          ctx.strokeRect(left, expectedY, barWidth, height);
          ctx.restore();
        }

        if (expected > actual) {
          const left = x - barWidth / 2;
          const right = x + barWidth / 2;

          ctx.save();
          ctx.strokeStyle = "#797979";
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 2]);

          ctx.beginPath();
          ctx.moveTo(left, expectedY + 4);
          ctx.lineTo(left, actualY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(right, expectedY + 4);
          ctx.lineTo(right, actualY);
          ctx.stroke();
          ctx.restore();
        }
      });
    },
  };
  const maxY = Number(
    chartData.reduce((acc, { expected, actual }) => {
      const max = expected > actual ? expected : actual;
      return acc > max ? acc : max;
    }, 0),
  );

  const options: ChartOptions<keyof ChartTypeRegistry> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: (context) => {
          const chart = context.chart;
          const tooltip = context.tooltip;

          let tooltipEl = tooltipRef.current;

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "custom-tooltip";
            tooltipEl.style.position = "absolute";
            tooltipEl.style.background = "#FFFFFF";
            tooltipEl.style.color = "#232323";
            tooltipEl.style.padding = "8px";
            tooltipEl.style.borderRadius = "4px";
            tooltipEl.style.pointerEvents = "none";
            tooltipEl.style.transform = "translate(-50%, -100%)";
            tooltipEl.style.fontFamily = "Inter, sans-serif";
            tooltipEl.style.display = "none";
            tooltipEl.style.zIndex = "99999";
            tooltipEl.style.minWidth = "250px";
            tooltipEl.style.boxShadow = `
                0px 0px 16px -4px #0000001A,
                0px 10px 15px -3px #0000001A,
                0px -1px 4px 0px #0000000D
              `;

            document.body.appendChild(tooltipEl);
            tooltipRef.current = tooltipEl;
          }

          if (tooltip.opacity === 0 && activeIndexRef.current === null) {
            tooltipEl.style.display = "none";
            return;
          }

          let index: number | null = null;

          if (tooltip.dataPoints?.length) {
            index = tooltip.dataPoints[0].dataIndex;
          } else if (activeIndexRef.current !== null) {
            index = activeIndexRef.current;
          }

          if (index === null) {
            tooltipEl.style.display = "none";
            return;
          }

          const item = chartData[index];
          if (!item) return;

          const { color, expected, actual, label, role } = item;

          const priority = priorityActionTags[
            keyRolesItems[role].priority as PriorityActionType
          ] as {
            label: string;
            color: string;
            borderColor: string;
            backgroundColor: string;
          };

          const actionTag = priorityActionTags[
            keyRolesItems[role].actionTag as PriorityActionType
          ] as {
            label: string;
            color: string;
            borderColor: string;
            backgroundColor: string;
          };

          const priorityTag = `<span style="font-size: 12px; line-height: 13px; font-weight: 500; border-radius: 16px; padding: 6px 12px; border: 1px solid ${priority.borderColor}; color:${priority.color}; background:${priority.backgroundColor};">${priority.label}</span>`;
          const actionTagTag = `<span style="font-size: 12px; line-height: 13px; font-weight: 500; border-radius: 16px; padding: 6px 12px; border: 1px solid ${actionTag.borderColor}; color:${actionTag.color}; background:${actionTag.backgroundColor};">${actionTag.label}</span>`;

          tooltipEl.innerHTML = `
            <div style="display:flex; flex-direction: column; gap:8px;font-size: 12px; line-height: 18px;
             position: relative">
              <div style="display:flex; align-items:center; gap:6px;">
                <span style="width:12px; height:12px; background:${color};"></span>
                <span style="font-weight: 500; font-size: 14px; line-height: 26px;">${label}</span>
              </div>
              <div style="display:flex; align-items:center; gap:8px;">
                ${priorityTag}${actionTagTag}
              </div>
              <div style="height:1px; background:#e5e7eb; width:100%; margin: 4px 0;"></div>
              
              <div style="display:flex; flex-direction: column; gap:8px;">
                <div style="display:flex; justify-content: space-between; gap: 12px;">Identified: <span style="font-weight: 500">${actual}</span></div>
                <div style="display:flex; justify-content: space-between; gap: 12px;">Benchmark: <span style="font-weight: 500">${expected}</span></div>
              </div>
              
              ${
                actual !== expected
                  ? `<div style="display:flex; justify-content: space-between; color:${actual < expected ? "#CF3E00" : "#27AE60"}; padding-top: 4px">
                    ${actual - expected > 0 ? "Extra" : "Gap"}: <span style="font-weight: 500">${actual - expected > 0 ? "+" : ""}${actual - expected}</span></div>`
                  : ""
              }
              <div style="position: absolute; width: 0; height: 0; left: 50%; bottom: -14px; transform: translateX(-50%);
                border-left: 6px solid transparent; border-right: 6px solid transparent; border-top: 6px solid white;
                "></div>
            </div>
          `;

          const canvasRect = chart.canvas.getBoundingClientRect();

          let x = tooltip.caretX;
          let y = tooltip.caretY;

          if (!tooltip.dataPoints?.length) {
            const meta = chart.getDatasetMeta(0);
            const el = meta.data[index];

            if (el) {
              x = el.x;
              y = el.y;
            }
          }

          tooltipEl.style.left =
            canvasRect.left + window.pageXOffset + x + "px";
          tooltipEl.style.top =
            canvasRect.top + window.pageYOffset + y - 12 + "px";

          tooltipEl.style.display = "block";
        },
      },
    },
    scales: {
      x: {
        type: "category",
        labels,
        ticks: {
          color: "#121212",
          font: { size: 12, weight: 600 },
          align: "center",
          callback: function (value) {
            const label = this.getLabelForValue(value as number);
            const ctx = this.chart.ctx;

            const chartArea = this.chart.chartArea;
            if (!chartArea) {
              return label;
            }

            const totalWidth = chartArea.right - chartArea.left;
            const maxWidth = totalWidth / this.chart.data.labels!.length - 4;

            const words = label.split(" ");
            const lines: string[] = [];
            let currentLine = "";

            words.forEach((word) => {
              const testLine = currentLine ? currentLine + " " + word : word;
              const metrics = ctx.measureText(testLine);
              if (metrics.width > maxWidth) {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
              } else {
                currentLine = testLine;
              }
            });

            if (currentLine) lines.push(currentLine);

            return lines;
          },
        },
        border: { display: false },
      },
      y: {
        type: "linear",
        max: maxY,
        title: {
          display: true,
          text: "Identified Contacts Per Role",
          color: "#121212",
          font: { size: 16, weight: 600 },
        },
        ticks: {
          color: "#121212",
          font: { size: 16, weight: 500 },
        },
        border: { display: false },
      },
    },
  };

  const openHandler = () => {
    getBubbleChart({
      id,
      isAccounts,
      limit: searchDict.limit ? Number(searchDict?.limit) : undefined,
      size: searchDict.sizes?.split(QUERY_SEPARATOR),
      search: searchValueDebounce || undefined,
      industry: searchDict.industries?.split(QUERY_SEPARATOR),
      region: searchDict.regions?.split(QUERY_SEPARATOR),
      sort: searchDict.sort?.split("-")[0],
      order: searchDict.sort?.split("-")[1] as Order,
      start_date: searchDict.date?.split(QUERY_SEPARATOR)[0],
      end_date: searchDict.date?.split(QUERY_SEPARATOR)[1],
      indicator_timeframe: searchDict.period
        ? (searchDict.period.split(" ")[1].toLowerCase() as IndicatorTimeframe)
        : undefined,
    })
      .unwrap()
      .then(() => setIsOpen(true));
  };

  const handleLegendClick = (role: string) => {
    const chart = chartRef.current;
    if (!chart) return;

    const index = chartData.findIndex((d) => d.label === role);

    if (index === -1) {
      legendHoverActiveRef.current = false;
      activeIndexRef.current = null;
      setActiveIndex(null);
      setSelectedRole("");

      chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
      chart.setActiveElements([]);
      chart.update();
      return;
    }

    legendHoverActiveRef.current = true;
    activeIndexRef.current = index;
    setActiveIndex(index);
    setSelectedRole(role);

    const meta = chart.getDatasetMeta(0);
    const el = meta.data[index];

    if (!el) return;

    chart.setActiveElements([{ datasetIndex: 0, index }]);

    chart.tooltip?.setActiveElements([{ datasetIndex: 0, index }], {
      x: el.x,
      y: el.y,
    });

    chart.update();
  };

  return (
    <>
      <ReuseTooltip
        text={
          (isDisabled || isNotActiveRole) &&
          (isNotActiveRole
            ? "No active Roles found."
            : "Analysis required first.")
        }
        hideChildren={false}
      >
        <CustomButton
          onClick={openHandler}
          variant="outlined"
          customColor="#FFFFFF"
          textColor="#1D3E70"
          customSize="fit"
          fullWidth
          type="button"
          startIcon={<ChartIcon />}
          loading={isLoading}
          sx={{
            height: "32px",
            borderColor: "#1D3E70",
            minWidth: "162px",
            maxWidth: "162px",
          }}
          disabled={isDisabled || isNotActiveRole}
        >
          Role Gap Chart
        </CustomButton>
      </ReuseTooltip>

      {isOpen && (
        <BaseModal
          sx={{
            padding: "32px",
            width: "100%",
            maxWidth: "min(1340px, Calc(100vw - 100px))",
          }}
          open={isOpen && !isLoading}
          onClose={() => setIsOpen(false)}
          title={`${title} Gap Chart`}
        >
          <Stack gap="24px" ref={setModalContentEl}>
            <Typography
              variant="body1"
              color="text.secondary"
              paddingRight={"24px"}
            >
              The buying committee gap chart compares the number of identified
              members per role to the expected number. Solid-colored bars show
              discovered members, the cutoff line marks the target, and hatched
              segments show the excess above target. Values below the line
              indicate gaps in role coverage, while values above indicate role
              overrepresentation.
            </Typography>
            {currentData && (
              <ChartAnalyticsBlock
                average_coverage={currentData.analytics.average_coverage}
                bc_members_identified={
                  currentData.analytics.bc_members_identified
                }
                roles_below_benchmark={
                  currentData.analytics.roles_below_benchmark
                }
                total_contacts_below_benchmark={
                  currentData.analytics.total_contacts_below_benchmark
                }
                total_roles={currentData.roles.length}
              />
            )}

            <Stack flexDirection="row" borderBottom="1px solid #EEEEEE">
              {chartTabs.map(({ key, label }) => (
                <Stack
                  key={key}
                  onClick={() => setTab(key)}
                  sx={{
                    display: "flex",
                    padding: "4px 16px",
                    alignItems: "center",
                    cursor: "pointer",
                    borderBottom:
                      tab === key
                        ? "2px solid #1C3C6C"
                        : "2px solid transparent",
                  }}
                >
                  <Typography
                    sx={{
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontFamily: "Inter",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "26px",
                      letterSpacing: "0.25px",
                      textTransform: "capitalize",
                      color: tab === key ? "#1C3C6C" : "#797979",
                    }}
                  >
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Stack sx={{ height: CHART_AREA_HEIGHT, minHeight: 0 }}>
              {tab === "role" ? (
                <Stack
                  flexDirection="row"
                  gap="16px"
                  flexGrow={1}
                  height="100%"
                >
                  <Stack flexGrow={1}>
                    <Chart
                      ref={chartRef}
                      type="bar"
                      data={data}
                      options={options}
                      plugins={[customBarPlugin]}
                    />
                  </Stack>
                  <BubbleLegendBlock
                    roles={chartData}
                    selectedRole={selectedRole}
                    handleLegendClick={handleLegendClick}
                    average_coverage={
                      currentData?.analytics.average_coverage || 0
                    }
                  />
                </Stack>
              ) : (
                <PersonaGapChart
                  personas={currentData?.personas || []}
                  boundaryEl={modalContentEl}
                />
              )}
            </Stack>
          </Stack>
        </BaseModal>
      )}
    </>
  );
};
